import type { PagedContentObject, PageMetadata } from '@/models/response-types'

/**
 * Build a `FormData` body for multipart/form-data requests from a plain object.
 *
 * - `File` / `Blob` values are appended as-is (the binary file part).
 * - `boolean` / `number` values are stringified so the BE receives `"true"`, `"42"`, etc.
 * - `undefined` / `null` entries are skipped so optional fields aren't sent as empty.
 * - everything else is coerced to `String`.
 */
export const buildFormData = (payload: Record<string, unknown>): FormData => {
  const formData = new FormData()
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    if (value instanceof Blob) {
      formData.append(key, value)
    } else {
      formData.append(key, String(value))
    }
  })
  return formData
}

/**
 * Backend enum-validation failures arrive as raw internals, e.g.
 * "Invalid enum value for the field: 'reason'. The value must be one of: [DAMAGED, EXPIRED, ...]".
 * Never show that verbatim — surface a short, user-friendly message instead.
 */
const RAW_ENUM_ERROR_PATTERN = /invalid enum value for the field:\s*'([^']+)'/i

const humanizeValidationMessage = (message: string): string => {
  const enumError = RAW_ENUM_ERROR_PATTERN.exec(message)
  if (enumError) {
    // camelCase field name → spaced label ("strengthUnit" → "strength unit").
    const field = enumError[1].replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase()
    return `The selected ${field} is not supported. Please choose a valid option and try again.`
  }
  return message
}

const extractResponseMessage = (r: {
  message?: string | null
  statusText?: string | null
  data?: {
    message?: string | null
    error?: string | null
    errorMessage?: string | null
    detail?: string | null
    errors?: Array<string | { message?: string; defaultMessage?: string } | undefined> | string
  }
}): string | null => {
  // BE envelope first — most of our endpoints follow this shape.
  if (r.data?.message) return r.data.message
  if (r.data?.error) return r.data.error
  // Video endpoints (/video/token, /video/join-info) return `errorMessage` at the top level.
  if (r.data?.errorMessage) return r.data.errorMessage
  if (r.data?.errors) {
    if (typeof r.data.errors === 'string') return r.data.errors
    if (Array.isArray(r.data.errors)) {
      const first = r.data.errors[0]
      if (typeof first === 'string') return first
      if (first && typeof first === 'object') {
        if (first.message) return first.message
        if (first.defaultMessage) return first.defaultMessage
      }
    }
  }
  if (r.data?.detail) return r.data.detail

  // Fall back to the underlying axios / JS error fields.
  if (r.message) return r.message
  if (r.statusText) return r.statusText

  return null
}

export const getResponseMessage = (response: unknown, fallback: string): string => {
  if (!response || typeof response !== 'object') return fallback

  const message = extractResponseMessage(response)

  if (!message) {
    console.warn(
      '[getResponseMessage] no extractable message — falling back to default. Raw:',
      response,
    )
    return fallback
  }

  return humanizeValidationMessage(message)
}

/**
 * Pull the machine-readable error `code` out of the BE envelope, e.g.
 * "ROOM_ALREADY_BOOKED". The axios interceptor rejects with `error.response`,
 * so on failures the envelope sits at `.data` — the same place
 * `extractResponseMessage` reads `message` from. Returns null when there is no
 * usable code (network failure, non-envelope error), so callers must always
 * keep a `getResponseMessage` fallback. Success envelopes carry a `code` too;
 * this helper is meant for error branching, not success handling.
 */
export const getResponseCode = (response: unknown): string | null => {
  if (!response || typeof response !== 'object') return null
  const { data } = response as { data?: { code?: unknown } }
  const code = data?.code
  return typeof code === 'string' && code.trim().length > 0 ? code : null
}

/**
 * True when a rejected request was denied rather than merely failed.
 *
 * The axios interceptor rejects with `error.response`, so the HTTP status sits at
 * `.status` on the value callers catch. Clinic-scoped endpoints answer 403 (with
 * `code: "ACCESS_DENIED"`) for a clinic the caller is not assigned to. Denials are
 * a state, not a transient error: never retry one, and branch on the status rather
 * than matching the message, which varies by reason.
 */
export const isForbiddenResponse = (response: unknown): boolean => {
  if (!response || typeof response !== 'object') return false
  const { status } = response as { status?: unknown }
  return status === 403
}

/**
 * Coerce a paged list payload into `PagedContentObject` whatever shape the BE
 * ships it in. Spring serializes a page either nested (`{ content, page: {…} }`,
 * the documented shape) or flat (`{ content, totalElements, totalPages, size,
 * number }`, the legacy default), and a few endpoints answer with a bare
 * array. Reading `page.totalElements` off the flat shape yields `undefined`,
 * which the tables treat as "0 results" and silently hide the pagination bar
 * while still rendering the first page — so every list read that drives a
 * pagination control goes through here. Missing counts fall back to the rows
 * actually returned; an unrecognised payload becomes an empty page.
 */
export const toPagedContent = <T>(raw: unknown): PagedContentObject<T> => {
  const emptyPage = (): PagedContentObject<T> => ({
    content: [],
    page: { size: 0, number: 0, totalElements: 0, totalPages: 0 },
  })

  if (Array.isArray(raw)) {
    const content = raw as T[]
    return {
      content,
      page: { size: content.length, number: 0, totalElements: content.length, totalPages: 1 },
    }
  }

  if (!raw || typeof raw !== 'object') return emptyPage()

  const { content, page, totalElements, totalPages, size, number } = raw as {
    content?: unknown
    page?: Partial<PageMetadata> | null
    totalElements?: unknown
    totalPages?: unknown
    size?: unknown
    number?: unknown
  }
  if (!Array.isArray(content)) return emptyPage()

  const rows = content as T[]
  const asCount = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) ? value : undefined

  const resolvedTotal = asCount(page?.totalElements) ?? asCount(totalElements) ?? rows.length
  const resolvedSize = asCount(page?.size) ?? asCount(size) ?? rows.length
  const resolvedNumber = asCount(page?.number) ?? asCount(number) ?? 0
  const resolvedPages =
    asCount(page?.totalPages) ??
    asCount(totalPages) ??
    (resolvedSize > 0 ? Math.ceil(resolvedTotal / resolvedSize) : rows.length ? 1 : 0)

  return {
    content: rows,
    page: {
      size: resolvedSize,
      number: resolvedNumber,
      totalElements: resolvedTotal,
      totalPages: resolvedPages,
    },
  }
}
